import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { environment } from '../../../../../environments/environment';
import { AzureTenant } from '@shared/models/azureTenant';
import { TenantResponse } from '@shared/models/tenantResponse';
import { GetNameOfEmail } from '@shared/util/emailToName';
import { Validator } from '@shared/util/validator';
import { CreateTenantDTO } from '../../models/createTenanatDTO';
import { ApiService } from '../../services/api-service/api.service';
import { TenantService } from '../../services/tenant.service';
import { CreatedApplication } from '../../models/createdApplication';
import { ApplicationSecret } from '../../models/applicationSecret';
import { ErrorService } from '@shared/services/error/error.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { SESSION_STORAGE_KEYS } from 'app/core/session_storage/constants/session-storage-keys.constant';
import { setMsalTenantId } from 'app/core/auth/msal.factory';
import { DataService } from '@shared/services/data/data.service';
import { NgClass } from '@angular/common';
import { Bind } from 'primeng/bind';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { Chip } from 'primeng/chip';
import { ProgressSpinner } from 'primeng/progressspinner';

// Onboarding step interface
interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  errorMessage?: string;
  canRetry?: boolean;
  debugInfo?: any;
}

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
  imports: [NgClass, ReactiveFormsModule, Bind, InputText, Button, Chip, ProgressSpinner],
})
export class OnboardingComponent implements OnInit {
  dataFormGroup: FormGroup;

  tenants: AzureTenant[] = [];
  currentlySelected?: AzureTenant;
  tenantsToCreate: CreateTenantDTO[] = [];
  onboardingId: string = '';
  validOnboarding: boolean = false;

  // Step-based state management
  currentStepIndex: number = 0;
  steps: OnboardingStep[] = [];

  // Simplified state flags
  isProcessing: boolean = false;
  createdApplication?: CreatedApplication;
  boundFunctionReference?: any;

  constructor(
    private route: ActivatedRoute,
    private msalService: MsalService,
    private apiService: ApiService,
    private tenantService: TenantService,
    private errorService: ErrorService,
    private toastService: ToastService,
    private _formBuilder: FormBuilder,
    readonly dataService: DataService,
  ) {
    this.route.params.subscribe((s) => {
      this.onboardingId = s.id;
    });

    this.dataFormGroup = this._formBuilder.group({
      orgName: [
        '',
        {
          validators: [Validators.required, Validators.maxLength(30)],
        },
      ],
      licenseKey: ['', [Validators.required, Validators.maxLength(36), Validators.minLength(36)]],
    });
  }

  async ngOnInit(): Promise<void> {
    // Initialize steps with localized texts
    this.steps = [
      {
        id: 'setup',
        title: this.dataService.getTexts().OnboardingStepSetupTitle,
        description: this.dataService.getTexts().OnboardingStepSetupDescription,
        status: 'pending',
      },
      {
        id: 'portal-consent',
        title: this.dataService.getTexts().OnboardingStepPortalConsentTitle,
        description: this.dataService.getTexts().OnboardingStepPortalConsentDescription,
        status: 'pending',
        canRetry: true,
      },
      {
        id: 'backend-consent',
        title: this.dataService.getTexts().OnboardingStepBackendConsentTitle,
        description: this.dataService.getTexts().OnboardingStepBackendConsentDescription,
        status: 'pending',
        canRetry: true,
      },
      {
        id: 'create-service-principal',
        title: this.dataService.getTexts().OnboardingStepCreateServicePrincipalTitle,
        description: this.dataService.getTexts().OnboardingStepCreateServicePrincipalDescription,
        status: 'pending',
        canRetry: true,
      },
      {
        id: 'service-principal-consent',
        title: this.dataService.getTexts().OnboardingStepServicePrincipalConsentTitle,
        description: this.dataService.getTexts().OnboardingStepServicePrincipalConsentDescription,
        status: 'pending',
        canRetry: true,
      },
      {
        id: 'create-organization',
        title: this.dataService.getTexts().OnboardingStepCreateOrganizationTitle,
        description: this.dataService.getTexts().OnboardingStepCreateOrganizationDescription,
        status: 'pending',
        canRetry: true,
      },
    ];

    sessionStorage.setItem(SESSION_STORAGE_KEYS.ONBOARDING_ACTIVE, 'true');
    await this.confirmOnboarding();
    this.loadCurrentState();
    // Restore form and step from sessionStorage if present
    const orgName = sessionStorage.getItem('onboarding_orgName');
    if (orgName) this.dataFormGroup.controls.orgName.setValue(orgName);
    const licenseKey = sessionStorage.getItem('onboarding_licenseKey');
    if (licenseKey) this.dataFormGroup.controls.licenseKey.setValue(licenseKey);
    const stepIndex = sessionStorage.getItem('onboarding_stepIndex');
    if (stepIndex) this.currentStepIndex = +stepIndex;
    const selectedTenant = sessionStorage.getItem('currentlySelected');
    if (selectedTenant) this.currentlySelected = JSON.parse(selectedTenant);
  }

  // handle Portal Consent Messages
  private handlePortalConsentMessage(event: MessageEvent): void {
    // IMPORTANT: always check origin to prevent security issues
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data && event.data.type == 'consent-completed') {
      this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingConsentSuccessfullyCompleted);
      this.updateStepStatus('backend-consent', 'completed');

      // Remove the listener to avoid memory leaks.
      window.removeEventListener('message', this.handlePortalConsentMessage.bind(this.boundFunctionReference), false);
    }
  }

  // Handler for backend consent callback message.
  private handleBackendConsentMessage(event: MessageEvent): void {
    // IMPORTANT: always check origin to prevent security issues
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data && event.data.type == 'consent-completed') {
      this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingConsentSuccessfullyCompleted);
      this.updateStepStatus('backend-consent', 'completed');

      // Remove the listener to avoid memory leaks.
      window.removeEventListener('message', this.handleBackendConsentMessage.bind(this), false);
    }
  }

  // Handler for consent-callback messages
  private handlePrincipalConsentMessage(event: MessageEvent): void {
    // IMPORTANT: always check origin to prevent security issues
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data && event.data.type == 'consent-completed') {
      this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingConsentSuccessfullyCompleted);
      this.updateStepStatus('service-principal-consent', 'completed');

      // Remove the listener to avoid memory leaks
      window.removeEventListener('message', this.handlePrincipalConsentMessage.bind(this), false);
    }
  }

  // Step management methods
  private updateStepStatus(
    stepId: string,
    status: OnboardingStep['status'],
    errorMessage?: string,
    debugInfo?: any,
  ): void {
    const step = this.steps.find((s) => s.id === stepId);
    if (step) {
      step.status = status;
      step.errorMessage = errorMessage;
      step.debugInfo = debugInfo;
    }
    this.saveState();
  }

  private moveToNextStep(): void {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
      this.saveState();
    }
  }

  private saveState(): void {
    const state = {
      currentStepIndex: this.currentStepIndex,
      steps: this.steps,
      currentlySelected: this.currentlySelected,
      tenantsToCreate: this.tenantsToCreate,
      createdApplication: this.createdApplication,
      orgName: this.dataFormGroup.get('orgName')?.value,
      licenseKey: this.dataFormGroup.get('licenseKey')?.value,
    };
    sessionStorage.setItem('onboardingState', JSON.stringify(state));
  }

  private loadCurrentState(): void {
    const savedState = sessionStorage.getItem('onboardingState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.currentStepIndex = state.currentStepIndex || 0;
        this.steps = state.steps || this.steps;
        this.currentlySelected = state.currentlySelected;
        this.tenantsToCreate = state.tenantsToCreate || [];
        this.createdApplication = state.createdApplication;

        if (state.orgName) {
          this.dataFormGroup.get('orgName')?.setValue(state.orgName);
        }
        if (state.licenseKey) {
          this.dataFormGroup.get('licenseKey')?.setValue(state.licenseKey);
        }
      } catch (error) {
        console.error('Failed to load onboarding state:', error);
      }
    }
  }

  // Validating onboarding id and user
  async confirmOnboarding(): Promise<boolean> {
    if (Validator.validateId(this.onboardingId)) {
      const account = this.getPrimaryAccount();
      if (!account) {
        this.toastService.showWarningMessage(this.dataService.getTexts().OnboardingNotAllowedToCreateOrganization);
        return false;
      }

      const email = account.username;
      this.apiService.confirmOnboarding(this.onboardingId, email).subscribe({
        next: (confirm: boolean) => {
          if (confirm) {
            this.validOnboarding = true;
            this.loadTenants();
            return true;
          } else {
            this.toastService.showWarningMessage(this.dataService.getTexts().OnboardingNotAllowedToCreateOrganization);
            return false;
          }
        },
        error: (error: any) => {
          console.error('Onboarding confirmation failed:', error);
          this.toastService.showWarningMessage(this.dataService.getTexts().OnboardingNotAllowedToCreateOrganization);
          return false;
        },
      });
    } else {
      this.toastService.showWarningMessage(this.dataService.getTexts().OnboardingNotAllowedToCreateOrganization);
      return false;
    }
  }

  // Get all tenants of user
  loadTenants(): void {
    this.tenantService.getUserTenants().subscribe({
      next: (userTenants: TenantResponse) => {
        let tenants = userTenants.value;
        tenants.forEach((t) => {
          let newTenant = {
            tenantId: t.tenantId,
            displayName: t.displayName,
            tenantCategory: t.tenantCategory,
            organizations: [],
            orgText: '',
            frontendConsent: false,
            backendConsent: false,
            consent: false,
          } as AzureTenant;
          this.tenants.push(newTenant);
        });
      },
    });
  }

  // Step action methods
  setupStep(): void {
    if (!this.dataFormGroup.valid || !this.currentlySelected) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingFillRequiredFieldsAndSelectTenant);
      return;
    }

    this.updateStepStatus('setup', 'completed');
    this.moveToNextStep();
    this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingSetupCompletedSuccessfully);
  }

  /**
   * Portal Consent Step - Admin consent for Portal App (223f56ec-09c2-4b08-b484-3e24c518d50c)
   * Based on Microsoft Documentation: https://learn.microsoft.com/en-us/graph/tutorial-applications-basics#create-a-service-principal-for-an-application
   */
  portalConsentStep(): void {
    if (!this.currentlySelected) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingNoTenantSelected);
      return;
    }

    this.updateStepStatus('portal-consent', 'in-progress');

    const portalClientId = '223f56ec-09c2-4b08-b484-3e24c518d50c'; // Portal App Client ID (fixed)
    const tenant = this.currentlySelected;

    console.log('=== PORTAL CONSENT STEP ===');
    console.log('Portal Client ID:', portalClientId);
    console.log('Tenant ID:', tenant.tenantId);

    // Create/Check Service Principal via Microsoft Graph API first
    this.createServicePrincipalForApp(portalClientId, 'Token Control Portal')
      .then((servicePrincipal) => {
        console.log('Portal Service Principal ready:', servicePrincipal);

        // Then open admin consent URL
        const consentUrl = this.buildAdminConsentUrl(portalClientId, tenant.tenantId);
        console.log('Portal Consent URL:', consentUrl);

        this.updateStepStatus('portal-consent', 'in-progress', undefined, {
          consentUrl,
          clientId: portalClientId,
          tenantId: tenant.tenantId,
          servicePrincipal: servicePrincipal,
          step: 'admin-consent',
        });
        // Add the event listener for the consent events
        window.addEventListener('message', this.handlePortalConsentMessage.bind(this.boundFunctionReference), false);
        window.open(consentUrl, '_blank');
        this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingPortalServicePrincipalReady);
      })
      .catch((error) => {
        console.error('Portal SP creation/check failed:', error);
        this.updateStepStatus(
          'portal-consent',
          'error',
          `${this.dataService.getTexts().OnboardingFailedToPreparePortalServicePrincipal}: ${error.message}`,
          error,
        );
        this.toastService.showErrorMessage(
          `${this.dataService.getTexts().OnboardingFailedToPreparePortalServicePrincipal}: ${error.message}`,
        );
      });
  }

  /**
   * Backend Consent Step - Admin consent for Backend API (c08e2c98-a937-4642-837e-2105b3296830)
   */
  backendConsentStep(): void {
    if (!this.currentlySelected) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingNoTenantSelected);
      return;
    }

    this.updateStepStatus('backend-consent', 'in-progress');

    const backendClientId = 'c08e2c98-a937-4642-837e-2105b3296830'; // Backend API Client ID (fixed)
    const tenant = this.currentlySelected;

    console.log('=== BACKEND CONSENT STEP ===');
    console.log('Backend Client ID:', backendClientId);
    console.log('Tenant ID:', tenant.tenantId);

    // Create/Check Service Principal via Microsoft Graph API first
    this.createServicePrincipalForApp(backendClientId, 'Token Control Backend API')
      .then((servicePrincipal) => {
        console.log('Backend Service Principal ready:', servicePrincipal);

        // Then open admin consent URL
        const consentUrl = this.buildAdminConsentUrl(backendClientId, tenant.tenantId);
        console.log('Backend Consent URL:', consentUrl);

        this.updateStepStatus('backend-consent', 'in-progress', undefined, {
          consentUrl,
          clientId: backendClientId,
          tenantId: tenant.tenantId,
          servicePrincipal: servicePrincipal,
          step: 'admin-consent',
        });

        // Add event listener to listen for messages from the new window
        window.addEventListener('message', this.handleBackendConsentMessage.bind(this), false);
        window.open(consentUrl, '_blank');
        this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingBackendServicePrincipalReady);
      })
      .catch((error) => {
        console.error('Backend SP creation/check failed:', error);
        this.updateStepStatus(
          'backend-consent',
          'error',
          `${this.dataService.getTexts().OnboardingFailedToPrepareBackendServicePrincipal}: ${error.message}`,
          error,
        );
        this.toastService.showErrorMessage(
          `${this.dataService.getTexts().OnboardingFailedToPrepareBackendServicePrincipal}: ${error.message}`,
        );
      });
  }

  /**
   * Creates Service Principal for existing Application in customer tenant
   * Based on: https://learn.microsoft.com/en-us/graph/api/serviceprincipal-post-serviceprincipals
   */
  private async createServicePrincipalForApp(appId: string, displayName: string): Promise<any> {
    console.log(`Checking/Creating Service Principal for ${displayName} (${appId})`);

    return new Promise((resolve, reject) => {
      // First check if SP already exists
      this.apiService.getServicePrincipal(appId).subscribe({
        next: (response) => {
          if (response.value && response.value.length > 0) {
            const existingSP = response.value[0];
            console.log(`✅ Service Principal for ${displayName} already exists:`, existingSP);
            console.log(`   - Object ID: ${existingSP.id}`);
            console.log(`   - App ID: ${existingSP.appId}`);
            console.log(`   - Display Name: ${existingSP.displayName}`);
            resolve(existingSP);
          } else {
            console.log(`❌ Service Principal for ${displayName} not found, creating...`);
            // SP doesn't exist, create it
            this.apiService.createServicePrincipal(appId).subscribe({
              next: (newSP) => {
                console.log(`✅ Service Principal for ${displayName} created:`, newSP);
                console.log(`   - Object ID: ${newSP.id}`);
                console.log(`   - App ID: ${newSP.appId}`);
                console.log(`   - Display Name: ${newSP.displayName}`);
                resolve(newSP);
              },
              error: (createError) => {
                console.error(`❌ Failed to create SP for ${displayName}:`, createError);
                reject(createError);
              },
            });
          }
        },
        error: (error) => {
          console.error(`❌ Error checking SP for ${displayName}:`, error);
          reject(error);
        },
      });
    });
  }

  /**
   * Creates a new Application and Service Principal for automated access
   */
  createServicePrincipalStep(): void {
    if (!this.currentlySelected) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingNoTenantSelected);
      return;
    }

    // Reset processing state and update step status
    this.isProcessing = true;
    this.updateStepStatus('create-service-principal', 'in-progress');

    const tenant = this.currentlySelected;
    setMsalTenantId(tenant.tenantId);

    console.log('Starting Service Principal creation for tenant:', tenant.displayName);

    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (this.isProcessing && this.steps[3].status === 'in-progress') {
        console.warn('Service Principal creation timeout - operation taking too long');
        this.handleStepError('create-service-principal', this.dataService.getTexts().OnboardingOperationTimedOut, {
          message: 'Service Principal creation took longer than expected',
          status: 408,
          statusText: 'Request Timeout',
        });
      }
    }, 60000); // 60 seconds timeout

    // Create new application with proper permissions
    this.apiService.createApplication(this.getCurrentConfirmUrl()).subscribe({
      next: async (app: CreatedApplication) => {
        try {
          clearTimeout(timeoutId); // Clear timeout on success
          this.createdApplication = app;
          console.log('Application created:', app);

          // Wait for application to be available
          await this.waitForApplicationAvailability(app.id);

          // Create Service Principal for the new application
          this.apiService.createServicePrincipal(app.appId).subscribe({
            next: (servicePrincipal) => {
              console.log('Service Principal created for new app:', servicePrincipal);

              // Add secret to the application
              this.apiService.addSecretToServicePrincipal(app.id).subscribe({
                next: async (secret: ApplicationSecret) => {
                  const tenantToCreate: CreateTenantDTO = {
                    name: tenant.displayName,
                    id: tenant.tenantId,
                    organizations: [],
                    clientId: app.appId,
                    clientSecret: secret.secretText,
                  };

                  this.tenantsToCreate = [tenantToCreate];
                  this.updateStepStatus('create-service-principal', 'completed', undefined, {
                    applicationId: app.id,
                    appId: app.appId,
                    servicePrincipal: servicePrincipal,
                    tenantToCreate: tenantToCreate,
                  });
                  this.moveToNextStep();
                  this.isProcessing = false;
                  this.toastService.showSuccessMessage(
                    this.dataService.getTexts().OnboardingServicePrincipalAndApplicationCreatedSuccessfully,
                  );
                },
                error: (error: any) => {
                  clearTimeout(timeoutId); // Clear timeout on error
                  console.error('Failed to add secret to application:', error);
                  this.handleStepError(
                    'create-service-principal',
                    this.dataService.getTexts().OnboardingFailedToAddSecretToApplication,
                    error,
                  );
                },
              });
            },
            error: (error: any) => {
              clearTimeout(timeoutId); // Clear timeout on error
              console.error('Failed to create service principal:', error);
              this.handleStepError(
                'create-service-principal',
                this.dataService.getTexts().OnboardingFailedToCreateServicePrincipalForNewApplication,
                error,
              );
            },
          });
        } catch (error: any) {
          clearTimeout(timeoutId); // Clear timeout on error
          console.error('Application verification failed:', error);
          this.handleStepError(
            'create-service-principal',
            this.dataService.getTexts().OnboardingApplicationVerificationFailed,
            error,
          );
        }
      },
      error: (error: any) => {
        clearTimeout(timeoutId); // Clear timeout on error
        console.error('Application creation failed:', error);
        this.handleStepError(
          'create-service-principal',
          this.dataService.getTexts().OnboardingApplicationCreationFailed,
          error,
        );
      },
    });
  }

  /**
   * Service Principal Consent Step - Admin consent for the newly created SP
   */
  servicePrincipalConsentStep(): void {
    if (!this.createdApplication || this.tenantsToCreate.length === 0) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingNoServicePrincipalToConsent);
      return;
    }

    this.updateStepStatus('service-principal-consent', 'in-progress');

    const tenant = this.tenantsToCreate[0];
    const consentUrl = this.buildAdminConsentUrl(tenant.clientId, tenant.id);

    this.updateStepStatus('service-principal-consent', 'in-progress', undefined, {
      consentUrl,
      clientId: tenant.clientId,
      tenantId: tenant.id,
      step: 'admin-consent',
    });
    // Add event listener to listen for messages from the new window
    window.addEventListener('message', this.handlePrincipalConsentMessage.bind(this), false);
    window.open(consentUrl, '_blank');
    this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingCompleteConsentInNewWindow);
  }

  /**
   * Final step - Create organization in backend
   */
  createOrganizationStep(): void {
    if (this.tenantsToCreate.length === 0) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingNoTenantDataForOrganizationCreation);
      return;
    }

    this.updateStepStatus('create-organization', 'in-progress');
    this.isProcessing = true;

    const account = this.getPrimaryAccount();
    if (!account) {
      this.toastService.showErrorMessage(this.dataService.getTexts().OnboardingNoActiveAccountFound);
      this.isProcessing = false;
      return;
    }

    const body = {
      OnboardingId: this.onboardingId,
      CustomerEmail: account.username,
      CustomerName: GetNameOfEmail.getNameOfEmail(account.username),
      OrganizationName: this.dataFormGroup.controls.orgName.value,
      Tenants: this.tenantsToCreate,
      LicenseKey: this.dataFormGroup.controls.licenseKey.value,
    };

    this.apiService.finishOnboarding(body).subscribe({
      next: (result: any) => {
        this.isProcessing = false;
        if (result) {
          this.updateStepStatus('create-organization', 'completed');
          sessionStorage.clear();
          sessionStorage.setItem('newOrg', this.dataFormGroup.controls.orgName.value);
          this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingOrganizationCreatedSuccessfully);
        } else {
          this.handleStepError(
            'create-organization',
            this.dataService.getTexts().OnboardingOrganizationCreationReturnedFalse,
            new Error('Creation failed'),
          );
        }
      },
      error: (error: any) => {
        this.handleStepError(
          'create-organization',
          this.dataService.getTexts().OnboardingOrganizationCreationFailed,
          error,
        );
      },
    });
  }

  // Helper methods
  private buildAdminConsentUrl(clientId: string, tenantId: string): string {
    const redirectUri = encodeURIComponent(`${window.location.origin}/consent`);
    // Using v1.0 endpoint for better compatibility (based on Microsoft docs)
    return `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`;
  }

  private getPrimaryAccount(): AccountInfo | undefined {
    return this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0];
  }

  private async waitForApplicationAvailability(appId: string): Promise<boolean> {
    const maxRetries = 10;
    const delay = 2000; // 2 seconds

    for (let i = 0; i < maxRetries; i++) {
      try {
        await this.apiService.getApplication(appId).toPromise();
        return true;
      } catch (error) {
        console.log(`Application not yet available, retry ${i + 1}/${maxRetries}`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    throw new Error('Application did not become available within expected time');
  }

  private handleStepError(stepId: string, message: string, error: any): void {
    this.isProcessing = false;
    console.error(`${message}:`, error);

    const errorDetails = {
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      error: error.error,
      timestamp: new Date().toISOString(),
    };

    console.error('Error details:', errorDetails);

    const errorMessage = error.message || error.statusText || 'Unknown error';
    this.updateStepStatus(stepId, 'error', `${message}: ${errorMessage}`, errorDetails);
    this.toastService.showErrorMessage(`${message}: ${errorMessage}`);
    this.errorService.handleError(error);
  }

  // Method to mark consent steps as completed manually
  markStepAsCompleted(stepId: string): void {
    this.isProcessing = false;
    this.updateStepStatus(stepId, 'completed');
    this.moveToNextStep();
    this.toastService.showSuccessMessage(this.dataService.getTexts().OnboardingStepMarkedAsCompleted);
  }

  // Method to cancel current operation
  cancelCurrentOperation(): void {
    this.isProcessing = false;
    const currentStep = this.steps[this.currentStepIndex];
    if (currentStep.status === 'in-progress') {
      this.updateStepStatus(currentStep.id, 'pending');
      this.toastService.showInformationMessage(this.dataService.getTexts().OnboardingOperationCancelled);
    }
  }

  // Method to retry failed steps
  retryStep(stepId: string): void {
    // Reset processing state and clear any previous errors
    this.isProcessing = false;
    this.updateStepStatus(stepId, 'pending');

    // Add a small delay to ensure UI updates properly
    setTimeout(() => {
      switch (stepId) {
        case 'portal-consent':
          this.portalConsentStep();
          break;
        case 'backend-consent':
          this.backendConsentStep();
          break;
        case 'create-service-principal':
          this.createServicePrincipalStep();
          break;
        case 'service-principal-consent':
          this.servicePrincipalConsentStep();
          break;
        case 'create-organization':
          this.createOrganizationStep();
          break;
        default:
          console.warn(`Unknown step ID for retry: ${stepId}`);
      }
    }, 100);
  }

  // Method to navigate to admin portal after completion
  navigateToAdminPortal(): void {
    window.open(environment.frontendUrl + '/admin/dashboard', '_blank');
  }

  // Utility methods
  selectedTenant(tenant: AzureTenant): void {
    // Speichere alle Formulardaten und Step-Index vor dem Reload
    sessionStorage.setItem('onboarding_orgName', this.dataFormGroup.controls.orgName.value);
    sessionStorage.setItem('onboarding_licenseKey', this.dataFormGroup.controls.licenseKey.value);
    sessionStorage.setItem('onboarding_stepIndex', this.currentStepIndex.toString());
    sessionStorage.setItem('currentlySelected', JSON.stringify(tenant));

    setMsalTenantId(tenant.tenantId);
    if (localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID) !== tenant.tenantId) {
      localStorage.setItem(SESSION_STORAGE_KEYS.TENANT_ID, tenant.tenantId);
      window.location.reload();
    }
    this.currentlySelected = tenant;
  }

  getCurrentConfirmUrl(): string {
    // Use current origin for dynamic redirect URI support (local testing & production)
    return window.location.origin + '/consent';
  }

  getErrorMessage(): string {
    let name = this.dataFormGroup.controls.orgName.value;
    if (name == '') return this.dataService.getTexts().OnboardingValidationPleaseEnterName;
    if (name.length > 30) return this.dataService.getTexts().OnboardingValidationMaxLength;
    return '';
  }

  getMobileStepOfText(): string {
    return this.dataService
      .getTexts()
      .OnboardingMobileStepOf.replace('{current}', String(this.currentStepIndex + 1))
      .replace('{total}', String(this.steps.length));
  }

  // UI helper methods for step display
  getStepIcon(status: OnboardingStep['status']): string {
    switch (status) {
      case 'completed':
        return 'pi pi-check';
      case 'in-progress':
        return 'pi pi-spin pi-spinner';
      case 'error':
        return 'pi pi-times';
      default:
        return 'pi pi-circle';
    }
  }

  getStepIconClass(status: OnboardingStep['status']): string {
    switch (status) {
      case 'completed':
        return 'step-completed';
      case 'in-progress':
        return 'step-in-progress';
      case 'error':
        return 'step-error';
      default:
        return 'step-pending';
    }
  }

  // Debug helper
  showDebugInfo(step: OnboardingStep): void {
    if (step.debugInfo) {
      console.log(`Debug info for ${step.id}:`, step.debugInfo);
      alert(`Debug info for ${step.title}:\n${JSON.stringify(step.debugInfo, null, 2)}`);
    }
  }
}
